import { useState } from "react";
import { motion } from "framer-motion";
import { articles, parsedDate } from "@/lib/utils";
import { ArrowRight, Search, Calendar, Filter } from "lucide-react";

const Articles = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");

  // Sort articles by date (newest first)
  const sortedArticles = [...articles].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Filter articles based on search
  const filteredArticles = sortedArticles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDate = !selectedDate || article.date.includes(selectedDate);
    return matchesSearch && matchesDate;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1E3D1A] text-white">
        <div className="absolute inset-0 bg-[url('/images/carrousel3.webp')] opacity-50" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Actualités du PMN
            </h1>
            <p className="text-xl text-white/90">
              Découvrez les dernières nouvelles et événements du Projet Mobilier
              National
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row-reverse gap-8">
          {/* Sidebar */}
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={`lg:w-96 flex-shrink-0 ${
              isSidebarOpen ? "block" : "hidden lg:block"
            }`}
          >
            <div className="bg-white rounded-2xl shadow-xl p-8 sticky top-8">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-[#1E3D1A]">Filtres</h2>
                <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className="lg:hidden p-2 rounded-full hover:bg-gray-100"
                >
                  <Filter className="w-5 h-5" />
                </button>
              </div>

              {/* Search */}
              <div className="relative mb-8">
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 pl-14 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1E3D1A] focus:border-transparent text-lg"
                />
                <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>

              {/* Date Filter */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Date
                </h3>
                <div className="space-y-3">
                  {["2025","2024", "2023",].map((year) => (
                    <button
                      key={year}
                      onClick={() =>
                        setSelectedDate(selectedDate === year ? "" : year)
                      }
                      className={`w-full px-6 py-3 rounded-xl text-left transition-colors duration-300 ${
                        selectedDate === year
                          ? "bg-[#1E3D1A] text-white"
                          : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Statistiques
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-base">
                    <span className="text-gray-600">Articles trouvés</span>
                    <span className="font-semibold text-[#1E3D1A]">
                      {filteredArticles.length}
                    </span>
                  </div>
                  <div className="flex justify-between text-base">
                    <span className="text-gray-600">Total articles</span>
                    <span className="font-semibold text-[#1E3D1A]">
                      {articles.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Articles Grid */}
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredArticles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 group"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center gap-4 text-white/90 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{parsedDate(article.date)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#1E3D1A] mb-3 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {article.description}
                    </p>
                    <a
                      href={`/actualites/${article.slug}`}
                      className="inline-flex items-center text-[#B89C00] hover:text-[#B89C00]/80 transition-colors duration-300 font-medium"
                    >
                      Lire la suite
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <p className="text-gray-600 text-lg">
                  Aucun article ne correspond à votre recherche.
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Articles;
